import React, { useState, useEffect } from "react";
import { FormikConfig, FormikValues, Formik, Form } from "formik";
import { Button, Stepper, Step, StepLabel, Grid } from "@mui/material";
import { Alert } from "@mui/material";
import useBreakpoints from "../Utils/Hooks/useBreakPoint";

interface FormikStepProps extends Pick<FormikConfig<FormikValues>, "children" | "validationSchema"> {
    label: string;
}

function FormikStep({ children }: FormikStepProps) {
    return <>{children}</>;
}

function FormikStepper({ children, ...props }: FormikConfig<FormikValues>) {
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const breakPoint = useBreakpoints();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    const childrenArray = React.Children.toArray(children) as React.ReactElement<FormikStepProps>[];

    //create a copy of the formikValues that were passed in via the step
    let formikLabel = childrenArray ? childrenArray[childrenArray.length - 1].props.label : null;
    let formikValues =
        formikLabel != null
            ? JSON.parse(JSON.stringify(formikLabel))
            : {
                  next: "next",
                  submit: "submit",
                  submitting: "submitting",
                  errorMessage: "Invalid inputs, please check your answers",
                  successfulSubmitMessage: "Form was successfully submitted!",
              };
    //remove the formikValues step
    childrenArray.pop();

    const currentChild = childrenArray[step] as React.ReactElement<FormikStepProps>;

    function isLastStep() {
        return step === childrenArray.length - 1;
    }

    return (
        <Formik
            {...props}
            validateOnChange={false}
            validationSchema={currentChild ? currentChild.props.validationSchema : null}
            onSubmit={async (values, helpers) => {
                if (isLastStep()) {
                    await props.onSubmit(values, helpers);
                } else {
                    setSubmitted(false);
                    setStep(step + 1);
                }
            }}
        >
            {({ isValid, isSubmitting }) => (
                <Form>
                    {currentChild}

                    {childrenArray.length !== 1 && (
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                            <Grid item xs={4} md={2}>
                                {step > 0 && (
                                    <Button
                                        style={{ marginTop: "20px" }}
                                        variant="contained"
                                        size="large"
                                        onClick={() => setStep(step - 1)}
                                    >
                                        Previous
                                    </Button>
                                )}
                            </Grid>
                            <Grid item xs={4} md={8}>
                                {childrenArray.length !== 1 && breakPoint != "xs" && breakPoint != "sm" && (
                                    <Stepper alternativeLabel activeStep={step}>
                                        {childrenArray.map((child, i) => (
                                            <Step completed={step > i}>
                                                <StepLabel></StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                )}
                            </Grid>
                            <Grid item xs={4} md={2}>
                                {isLastStep() ? (
                                    <Button
                                        style={{ marginTop: "20px" }}
                                        onClick={() => setSubmitted(true)}
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? formikValues["submitting"] : formikValues["submit"]}
                                    </Button>
                                ) : (
                                    <Button
                                        style={{ marginTop: "20px" }}
                                        variant="contained"
                                        size="large"
                                        onClick={() => setSubmitted(true)}
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        {formikValues["next"]}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {childrenArray.length === 1 && (
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                            <Button
                                style={{ margin: "60px 0px 20px 0px" }}
                                onClick={() => setSubmitted(true)}
                                variant="contained"
                                color="secondary"
                                size="large"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? formikValues["submitting"] : formikValues["submit"]}
                            </Button>
                        </Grid>
                    )}

                    {!isValid && submitted && (
                        <Grid style={{ marginTop: "7px" }}>
                            <Alert severity="error">{formikValues["errorMessage"]}</Alert>
                        </Grid>
                    )}
                </Form>
            )}
        </Formik>
    );
}

export { FormikStep, FormikStepper };
